/**
 * PostGoogleConsentGate — Feb 2026.
 *
 * Renders a blocking full-screen sheet AFTER Google OAuth returns if either
 * of these is missing on the user record:
 *   1. Guardian declaration (Terms §3, DPDP Act) — required for every user.
 *   2. A stored mobile number — Google gives us email + name only, not phone.
 *
 * The user cannot dismiss without either completing the form or signing out.
 * Once submitted:
 *   • Guardian declaration → POST /users/guardian-declaration (append-only)
 *   • Mobile → POST /users/me/mobile-unverified (no OTP; email already verified
 *     by Google, so the account is fully-usable immediately).
 *
 * If the account was created via the email/mobile sign-up flow, both fields
 * are already recorded and this component returns null on mount.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Loader2, AlertCircle, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  GUARDIAN_DECLARATION_TEXT,
  GUARDIAN_DECLARATION_VERSION,
  TERMS_VERSION_IN_EFFECT,
} from "@/constants/legalDisclosures";
import { toast } from "sonner";

// Key set by AuthModal → GoogleButton BEFORE the OAuth redirect. If present
// after callback we auto-attach silently so the user never has to re-enter it.
const PENDING_GOOGLE_MOBILE_KEY = "dhruva_pending_google_mobile";

function normaliseMobile(raw) {
  const digits = (raw || "").replace(/[^\d]/g, "");
  if ((raw || "").trim().startsWith("+")) return "+" + digits;
  if (digits.length === 10) return "+91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return "+" + digits;
  return digits ? "+" + digits : "";
}
const isMobileValid = (m) => /^\+\d{10,14}$/.test(normaliseMobile(m));

export default function PostGoogleConsentGate() {
  const { session } = useAuth();
  const [status, setStatus] = useState({ loading: true, needed: false });
  const [mobile, setMobile] = useState("");
  const [guardianDecl, setGuardianDecl] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // The site-wide advisory disclaimer (see DisclaimerModal.jsx) is also a
  // hard-blocking modal. If it mounts at the same time as this gate, two
  // dialogs stack on top of each other — confusing. We suppress the gate
  // until the disclaimer has been acknowledged. It's stored under
  // `cetp_disclaimer_v1_ack` in localStorage.
  const [disclaimerAcked, setDisclaimerAcked] = useState(() => {
    try { return !!localStorage.getItem("cetp_disclaimer_v1_ack"); } catch { return true; }
  });
  useEffect(() => {
    if (disclaimerAcked) return;
    // Poll once/second — the disclaimer sits at the top of AppLayout and
    // writes to localStorage when accepted; there's no global event bus.
    const t = setInterval(() => {
      try {
        if (localStorage.getItem("cetp_disclaimer_v1_ack")) {
          setDisclaimerAcked(true);
          clearInterval(t);
        }
      } catch (_) { /* privacy mode */ }
    }, 750);
    return () => clearInterval(t);
  }, [disclaimerAcked]);

  useEffect(() => {
    if (!session?.access_token) { setStatus({ loading: false, needed: false }); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/users/me");
        const needsConsent = !r.data?.guardian_declaration_recorded;
        let needsMobile = !(r.data?.mobile || r.data?.phone);

        // Auto-attach path: if the user typed a mobile BEFORE Google OAuth
        // (AuthModal → GoogleButton), consume it here so we don't ask again.
        if (needsMobile) {
          let stashed = "";
          try { stashed = localStorage.getItem(PENDING_GOOGLE_MOBILE_KEY) || ""; } catch { /* privacy */ }
          if (stashed && isMobileValid(stashed)) {
            try {
              await api.post("/users/me/mobile-unverified", { mobile: normaliseMobile(stashed) });
              needsMobile = false;
              try { localStorage.removeItem(PENDING_GOOGLE_MOBILE_KEY); } catch { /* privacy */ }
            } catch (attachErr) {
              // 409 → someone else owns the number. Keep the gate open so the
              // user can type a different one; clear the stash to avoid loops.
              try { localStorage.removeItem(PENDING_GOOGLE_MOBILE_KEY); } catch { /* privacy */ }
              const detail = attachErr?.response?.data?.detail;
              if (detail) {
                try { toast.error(detail); } catch { /* toast unavailable */ }
              }
            }
          }
        }

        if (!alive) return;
        setStatus({
          loading: false,
          needed: needsConsent || needsMobile,
          needsConsent,
          needsMobile,
        });
      } catch (_) {
        if (alive) setStatus({ loading: false, needed: false });
      }
    })();
    return () => { alive = false; };
  }, [session?.access_token]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (status.needsMobile && !isMobileValid(mobile)) {
      setErr("Enter a valid Indian mobile number.");
      return;
    }
    if (status.needsConsent && !guardianDecl) {
      setErr("Please tick the parent/guardian declaration to continue.");
      return;
    }
    setBusy(true);
    try {
      // Record consent first (append-only, idempotent).
      if (status.needsConsent) {
        await api.post("/users/guardian-declaration", {
          declaration_text: GUARDIAN_DECLARATION_TEXT,
          declaration_version: GUARDIAN_DECLARATION_VERSION,
          terms_version: TERMS_VERSION_IN_EFFECT,
          privacy_policy_version: TERMS_VERSION_IN_EFFECT,
        });
      }
      if (status.needsMobile) {
        try {
          await api.post("/users/me/mobile-unverified", { mobile: normaliseMobile(mobile) });
        } catch (mobileErr) {
          const detail = mobileErr?.response?.data?.detail || "Could not save mobile";
          setErr(detail);
          return;
        }
      }
      toast.success("You're all set. Welcome!");
      setStatus({ loading: false, needed: false });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Could not complete setup");
    } finally { setBusy(false); }
  };

  const cancelAndSignOut = async () => {
    try { await supabase.auth.signOut(); } catch (_) { /* noop */ }
    window.location.href = "/";
  };

  if (status.loading || !status.needed || !disclaimerAcked) return null;

  return (
    <Dialog open onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden"
        // Prevent all forms of dismissal — the user MUST either submit or sign out.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="post-google-consent-gate"
      >
        <DialogTitle className="sr-only">Complete your account setup</DialogTitle>
        <DialogDescription className="sr-only">
          We need your mobile number and a quick declaration to finish creating your account.
        </DialogDescription>

        <div className="p-6">
          <div className="w-10 h-10 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center mb-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900">One quick step</h2>
          <p className="text-[12px] text-slate-500 mt-1">
            We use this to send you payment receipts and prediction updates.
          </p>

          <form onSubmit={submit} className="mt-4 space-y-3">
            {status.needsMobile && (
              <div>
                <label className="text-[11px] font-semibold text-slate-700 mb-1 block">Mobile number</label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)}
                    placeholder="10-digit mobile (e.g. 91755 71090)"
                    className="h-11 pl-9 text-sm" autoComplete="tel" maxLength={15}
                    data-testid="post-google-mobile-input" autoFocus
                  />
                </div>
                <p className="mt-1 text-[10.5px] text-slate-500">
                  No OTP needed — you can verify later if you use mobile sign-in.
                </p>
              </div>
            )}

            {status.needsConsent && (
              <label htmlFor="post-google-guardian" className="flex items-start gap-2 cursor-pointer select-none rounded-lg border border-amber-300/60 bg-amber-50/40 px-2.5 py-2">
                <input
                  id="post-google-guardian" type="checkbox" checked={guardianDecl}
                  onChange={(e) => setGuardianDecl(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-slate-400 accent-amber-500 shrink-0"
                  data-testid="post-google-guardian-checkbox" required
                />
                <span className="text-[11px] leading-relaxed text-slate-700">
                  {GUARDIAN_DECLARATION_TEXT.split("Terms & Conditions and Privacy Policy")[0]}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline text-slate-900">Terms</Link>{" & "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-slate-900">Privacy</Link>.
                </span>
              </label>
            )}

            {err && (
              <div className="rounded-lg border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-800 flex items-start gap-1.5" data-testid="post-google-error">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
              disabled={busy}
              data-testid="post-google-continue-button"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {busy ? "Saving…" : "Continue"}
            </Button>

            <button
              type="button" onClick={cancelAndSignOut}
              className="w-full text-[11.5px] text-slate-500 hover:text-slate-800 inline-flex items-center justify-center gap-1"
              data-testid="post-google-cancel"
            >
              <LogOut className="w-3 h-3" /> Cancel and sign out
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
